import { GroupBase } from 'react-select';
import { useMemo, useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useCurrentUser } from '@/react/hooks/useUser';
import {
  HelmRegistrySelect,
  RegistryValue,
} from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { HelmTemplatesSelect } from '@/react/kubernetes/helm/components/HelmTemplatesSelect';
import { useHelmChartList } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartList';
import { Chart, HelmRelease } from '@/react/kubernetes/helm/types';
import {
  HELM_REPO_OPTION_LABEL,
  OCI_REGISTRY_OPTION_LABEL,
  useHelmRegistryRepoOptions,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRegistries';

import { FormSection } from '@@/form-components/FormSection';
import { Alert } from '@@/Alert';
import { Option } from '@@/form-components/PortainerSelect';
import { confirm } from '@@/modals/confirm';
import { buildConfirmButton } from '@@/modals/utils';

import { useIsRegistryAvailable } from './utils/useIsRegistryAvailable';
import { UpgradeHelmFormValues } from './types';

interface ChartReferenceFormSectionProps {
  chartReferenceInfoMessage?: string;
  formValues: UpgradeHelmFormValues;
  setValues: (values: UpgradeHelmFormValues) => void;
  formErrors: {
    registryId?: string;
    chart?: string;
  };
  namespace?: string;
  initialRegistryValue?: RegistryValue;
  hasVersionOptions: boolean;
  chartReferenceState: {
    requiresSourceConfirmation: boolean;
    requiresChartConfirmation: boolean;
  };
  releaseChartMetadata?: HelmRelease['chart']['metadata'];
  releaseChartPath?: string;
}

export function ChartReferenceFormSection({
  chartReferenceInfoMessage,
  formValues,
  setValues,
  formErrors,
  namespace,
  initialRegistryValue,
  hasVersionOptions,
  chartReferenceState,
  releaseChartMetadata,
  releaseChartPath,
}: ChartReferenceFormSectionProps) {
  const { registryId, repo, chart } = formValues;
  const { registryId: registrySelectError, chart: chartSelectError } =
    formErrors;

  const [hasConfirmedSourceChange, setHasConfirmedSourceChange] =
    useState(false);
  const [hasConfirmedChartChange, setHasConfirmedChartChange] = useState(false);
  const environmentId = useEnvironmentId();
  const { user } = useCurrentUser();

  // registry form state
  const registryOptionsQuery = useHelmRegistryRepoOptions(
    environmentId,
    namespace
  );
  const registryOptions = useRegistryOptions(
    registryOptionsQuery.registryOptions,
    initialRegistryValue
  );
  const selectedRegistry = useSelectedRegistry(
    registryOptions,
    registryId,
    repo
  );
  const isRegistryAvailable = useIsRegistryAvailable(
    selectedRegistry,
    registryOptions
  );

  // chart form state
  const chartListQuery = useHelmChartList(
    user.Id,
    environmentId,
    {
      id: registryId,
      url: repo,
    },
    !!selectedRegistry?.isOCI,
    true
  );
  // if there are versions available, the chart is valid.
  // in this case, we can use the release chart path as the initial option, instead of the long (10s+) wait to load the chart list
  const fallbackChartName = releaseChartPath || chart;
  const chartOptions: Chart[] =
    hasVersionOptions && !chartListQuery.data && fallbackChartName
      ? [
          {
            name: fallbackChartName,
            repo: repo || '',
            description: '',
            version: '',
            versions: [],
          },
        ]
      : (chartListQuery.data ?? []);

  const selectedChart = useSelectedChart(chartOptions, chart);

  return (
    <FormSection title="Helm chart">
      <ChartReferenceDetectInformation
        chartReferenceInfoMessage={chartReferenceInfoMessage}
        releaseMetadata={releaseChartMetadata}
      />
      <HelmRegistrySelect
        selectedRegistry={selectedRegistry}
        onRegistryChange={handleRegistrySelection}
        namespace={namespace}
        isRegistryAvailable={isRegistryAvailable}
        isLoading={registryOptionsQuery.isLoading}
        isLoadingError={registryOptionsQuery.isError}
        registryOptions={registryOptions}
        formError={registrySelectError}
        size="medium"
      />
      <HelmTemplatesSelect
        selectedChart={selectedChart}
        onSelect={handleChartSelection}
        isLoadingCharts={chartListQuery.isInitialLoading}
        charts={chartOptions}
        selectedRegistry={selectedRegistry}
        formError={getChartValidationMessage(
          chartOptions,
          chartSelectError,
          releaseChartMetadata?.name
        )}
        size="medium"
        isRegistryError={chartListQuery.isError}
        isOCI={selectedRegistry?.isOCI}
        registryId={selectedRegistry?.registryId}
      />
    </FormSection>
  );

  async function handleRegistrySelection(value: RegistryValue | null) {
    const isSameRegistry =
      (value?.registryId && value?.registryId === registryId) ||
      (value?.registryUrl && value?.registryUrl === repo);
    if (isSameRegistry) {
      return;
    }

    // Confirm changing the source if it's the initial value and already valid
    if (
      chartReferenceState.requiresSourceConfirmation &&
      !hasConfirmedSourceChange
    ) {
      const confirmed = await confirmChangeSource(
        selectedRegistry || undefined
      );
      if (!confirmed) {
        return;
      }
    }

    // Only confirm once
    setHasConfirmedSourceChange(true);
    setHasConfirmedChartChange(true);

    // Batch update all related fields to fix validation sync issues
    if (value?.registryId) {
      setValues({
        ...formValues,
        registryId: value.registryId,
        repo: '',
        chart: '',
        version: '',
      });
      return;
    }
    if (value?.registryUrl) {
      setValues({
        ...formValues,
        registryId: undefined,
        repo: value.registryUrl,
        chart: '',
        version: '',
      });
      return;
    }
    setValues({
      ...formValues,
      registryId: undefined,
      repo: '',
      chart: '',
      version: '',
    });
  }

  async function handleChartSelection(chart: Chart | null) {
    if (chart?.name === formValues.chart) {
      return;
    }

    // Confirm changing the chart if it's the initial value and already valid
    if (
      chartReferenceState.requiresChartConfirmation &&
      !hasConfirmedChartChange
    ) {
      const confirmed = await confirmChangeChart(formValues.chart || '');
      if (!confirmed) {
        return;
      }
    }
    // Only confirm once
    setHasConfirmedChartChange(true);

    // Batch update chart and version fields to fix validation sync issues
    setValues({
      ...formValues,
      chart: chart ? chart.name : '',
      version: '',
    });
  }
}

function ChartReferenceDetectInformation({
  chartReferenceInfoMessage,
  releaseMetadata,
}: {
  chartReferenceInfoMessage?: string;
  releaseMetadata?: HelmRelease['chart']['metadata'];
}) {
  if (!chartReferenceInfoMessage) {
    return null;
  }

  return (
    <Alert color="info" className="mb-5">
      <div className="flex flex-col gap-2">
        <div>{chartReferenceInfoMessage}</div>
        {releaseMetadata && (
          <div>
            <strong>Chart name:</strong> {releaseMetadata.name}
            <br />
            <strong>Chart version:</strong> {releaseMetadata.version}
            {releaseMetadata.appVersion &&
              ` (App version: ${releaseMetadata.appVersion})`}
          </div>
        )}
      </div>
    </Alert>
  );
}

function useSelectedRegistry(
  registryOptions: GroupBase<Option<RegistryValue>>[],
  registryId?: number,
  repoUrl?: string
): RegistryValue | null {
  return useMemo(() => {
    const allOptions = registryOptions.flatMap((option) => option.options);
    const valueWithRegistryId = allOptions.find(
      (option) => registryId && option.value.registryId === registryId
    );
    if (valueWithRegistryId) {
      return valueWithRegistryId.value;
    }
    const valueWithRepoUrl = allOptions.find(
      (option) => repoUrl && option.value.registryUrl === repoUrl
    );
    if (valueWithRepoUrl) {
      return valueWithRepoUrl.value;
    }
    return null;
  }, [registryOptions, registryId, repoUrl]);
}

function useSelectedChart(chartOptions: Chart[], chart: string): Chart | null {
  return useMemo(
    () => chartOptions.find((option) => option.name === chart) || null,
    [chartOptions, chart]
  );
}

/**
 * If the selected registry is a repo, is valid (already covered by useChartReferenceState) and is not in the registryOptions,
 * then add it to the repo options because we should allow upgrading in this case
 */
export function useRegistryOptions(
  registryOptions: GroupBase<Option<RegistryValue>>[],
  initialRegistryValue?: RegistryValue
) {
  return useMemo(() => {
    // if validated repoUrl isn't in the registryOptions, add it
    if (
      !initialRegistryValue ||
      initialRegistryValue.registryId ||
      !initialRegistryValue.registryUrl
    ) {
      return registryOptions;
    }
    const helmRepoOptions: GroupBase<Option<RegistryValue>> =
      registryOptions.find(
        (option) => option.label === HELM_REPO_OPTION_LABEL
      ) ?? {
        label: HELM_REPO_OPTION_LABEL,
        options: [],
      };

    const isSelectedRepoInRegistryOptions = helmRepoOptions.options.some(
      (option) => option.value.registryUrl === initialRegistryValue?.registryUrl
    );

    if (isSelectedRepoInRegistryOptions) {
      return registryOptions;
    }

    const OCIRegistryOptions: GroupBase<Option<RegistryValue>> =
      registryOptions.find(
        (option) => option.label === OCI_REGISTRY_OPTION_LABEL
      ) ?? {
        label: OCI_REGISTRY_OPTION_LABEL,
        options: [],
      };

    const newRepoOption: Option<RegistryValue> = {
      label: initialRegistryValue.name || initialRegistryValue.registryUrl,
      value: initialRegistryValue,
    };

    return [
      OCIRegistryOptions,
      {
        label: HELM_REPO_OPTION_LABEL,
        options: [newRepoOption, ...helmRepoOptions.options],
      },
    ];
  }, [registryOptions, initialRegistryValue]);
}

function getSourceChangeMessage(
  currentRegistry: RegistryValue | undefined
): string {
  if (!currentRegistry) {
    return 'The selected source has a chart that matches your release. Do you want to change it?';
  }

  if (currentRegistry.registryId) {
    return `The selected registry '${currentRegistry.name}' has a Helm chart that matches your release. Do you want to change it?`;
  }
  if (currentRegistry.registryUrl) {
    return `The selected repository '${currentRegistry.registryUrl}' has a Helm chart that matches your release. Do you want to change it?`;
  }

  return 'The selected source has a chart that matches your release. Do you want to change it?';
}

async function confirmChangeSource(
  currentRegistry: RegistryValue | undefined
): Promise<boolean> {
  return confirm({
    title: 'Are you sure?',
    message: getSourceChangeMessage(currentRegistry),
    confirmButton: buildConfirmButton('Continue', 'warning'),
    cancelButtonLabel: 'Cancel',
  });
}

async function confirmChangeChart(chartName: string): Promise<boolean> {
  return confirm({
    title: 'Are you sure?',
    message: `The selected Helm chart '${chartName}' already matches your release. Do you want to change it?`,
    confirmButton: buildConfirmButton('Continue', 'warning'),
    cancelButtonLabel: 'Cancel',
  });
}

/**
 * Finds a chart option that matches both the metadata name and registry.
 * @param chartOptions - Available charts from the registry
 * @param metadataChartName - Simple chart name from release.chart.metadata.name (e.g., "nginx")
 * @param registryUrl - The registry URL to match against
 * @returns The matching chart if exactly one match is found, undefined otherwise
 */
export function findChartByMetadataNameAndRegistry(
  chartOptions: Chart[],
  metadataChartName: string,
  registryUrl?: string
): Chart | undefined {
  const chartMatches = chartOptions.filter(
    (chartOption) =>
      doesChartPathEndWithName(metadataChartName, chartOption) &&
      chartOption.repo === registryUrl
  );

  if (chartMatches.length === 1) {
    return chartMatches[0];
  }
  // If there are multiple matching charts, selecting the wrong one could lead to errors from incorrect lookups.
  // Let the user select the chart if there are multiple matches (or if there are no matches).
  return undefined;
}

/**
 * Finds a chart option where the chart path ends with the metadata name.
 */
function findChartByMetadataName(
  chartOptions: Chart[],
  metadataChartName: string
): Chart | undefined {
  const chartMatches = chartOptions.filter((chartOption) =>
    doesChartPathEndWithName(metadataChartName, chartOption)
  );

  if (chartMatches.length === 1) {
    return chartMatches[0];
  }
  // If there are multiple matching charts, selecting the wrong one could lead to errors from incorrect lookups.
  // Let the user select the chart if there are multiple matches (or if there are no matches).
  return undefined;
}

/**
 * Checks if a chart's full path ends with the given metadata name.
 * For example, "bitnami/nginx" ends with "nginx".
 */
function doesChartPathEndWithName(
  metadataChartName: string,
  chartOption: Chart
): boolean {
  const chartPathParts = chartOption.name.split('/');
  const chartBaseName = chartPathParts[chartPathParts.length - 1];
  return chartBaseName === metadataChartName;
}

function getChartValidationMessage(
  chartOptions: Chart[],
  formError?: string,
  metadataChartName?: string
) {
  if (!metadataChartName || chartOptions.length === 0) {
    return formError;
  }

  const matchingChart = findChartByMetadataName(
    chartOptions,
    metadataChartName
  );

  if (!matchingChart) {
    return `There is no Helm chart that matches '${metadataChartName}' for the release. Do you have the right Helm chart source selected?`;
  }

  return formError;
}
